import React, { useState, useEffect, Suspense, useRef, lazy } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CogIcon, ShareIcon } from '@heroicons/react/20/solid';
import { TableCellsIcon, MegaphoneIcon, PaintBrushIcon, SwatchIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { getGameTypeColors } from '../utils/gameTypeColors';
import DefaultLoading from '../common/DefaultLoading';
import ScrollableTabBar from '../components/ScrollableTabBar';
import EditorTour from './EditorTour';

// Lazy load all section components
const StartScreenSection = lazy(() => import('./StartScreenSection'));
const LeaderboardSection = lazy(() => import('./LeaderboardSection'));
const CTASection = lazy(() => import('./CTASection'));
const AppearanceSection = lazy(() => import('./AppearanceSection'));
const ShareSection = lazy(() => import('./ShareSection'));

const Form = ({ options, onOptionsUpdate, onRequestPreviewReload, params, game, businessProfile, formRef, navCollapsed = false, onNavCollapsedChange, onSwitchGameType, allGameTypes = [], formApiRef }) => {
  const [activeSection, setActiveSection] = useState(() => {
    const justCreated = sessionStorage.getItem('gameJustCreated');
    const noTypePicked = sessionStorage.getItem('gameJustCreatedNoType');
    if (justCreated && noTypePicked) return 'gameType';
    if (justCreated) return 'appearance';
    return 'content';
  });
  const [GameSetupComponent, setGameSetupComponent] = useState(null);
  const [gameSetupLoadError, setGameSetupLoadError] = useState(null);
  const [gameSetupRetryKey, setGameSetupRetryKey] = useState(0);
  const [gameSetupAutoRetrying, setGameSetupAutoRetrying] = useState(false);
  const gameSetupAutoRetryCountRef = useRef(0);
  const mobileNavRef = useRef(null);

  useEffect(() => {
    sessionStorage.removeItem('gameJustCreated');
    sessionStorage.removeItem('gameJustCreatedNoType');
  }, []);

  // Expose imperative section navigation to the parent (Editor) so the
  // header overflow menu can switch sections (e.g. game type picker).
  useEffect(() => {
    if (formApiRef) {
      formApiRef.current = { setActiveSection };
    }
    return () => {
      if (formApiRef) formApiRef.current = null;
    };
  }, [formApiRef]);

  // Auto-scroll active tab into view on mobile
  useEffect(() => {
    const el = mobileNavRef.current;
    if (!el) return;
    const active = el.querySelector('[data-active-tab="true"]');
    if (active) active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeSection]);

  const isChunkLoadError = (error) => {
    const msg = String(error?.message || error || '').toLowerCase();
    return (
      msg.includes('loading chunk') ||
      msg.includes('chunkloaderror') ||
      msg.includes('failed to fetch dynamically imported module')
    );
  };

  const retryGameSetupLoad = () => {
    setGameSetupLoadError(null);
    setGameSetupAutoRetrying(false);
    setGameSetupRetryKey(k => k + 1);
  };

  // Reset auto-retry budget when the game type changes
  useEffect(() => {
    gameSetupAutoRetryCountRef.current = 0;
    setGameSetupAutoRetrying(false);
  }, [game?.gameType]);

  // Load GameSetup component when game type changes (or retry); clear when gameType is missing
  useEffect(() => {
    if (!game?.gameType) {
      setGameSetupComponent(null);
      setGameSetupLoadError(null);
      setGameSetupAutoRetrying(false);
      return;
    }
    setGameSetupLoadError(null);
    let cancelled = false;
    let retryTimer = null;
    import(`../games/${game.gameType}/GameSetup`)
      .then(module => {
        if (!cancelled) {
          setGameSetupComponent(() => module.default);
          setGameSetupLoadError(null);
          setGameSetupAutoRetrying(false);
        }
      })
      .catch(error => {
        console.error('Error loading GameSetup component', error);
        if (!cancelled) {
          setGameSetupComponent(null);
          setGameSetupLoadError(error);
          const canAutoRetry = isChunkLoadError(error) && gameSetupAutoRetryCountRef.current < 1;
          if (canAutoRetry) {
            gameSetupAutoRetryCountRef.current += 1;
            setGameSetupAutoRetrying(true);
            retryTimer = setTimeout(() => {
              if (cancelled) return;
              retryGameSetupLoad();
            }, 350);
          }
        }
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [game?.gameType, gameSetupRetryKey]);

  // Helper function to get section completion status
  const getSectionStatus = (sectionId) => {
    switch (sectionId) {
      case 'content':
        return (options?.title && options?.intro) ? 'complete' : 'incomplete';
      case 'gameSetup':
        return GameSetupComponent ? 'complete' : 'incomplete';
      case 'appearance':
        return (options?.theme?.colors || options?.theme?.fonts) ? 'complete' : 'incomplete';
      case 'header':
        return options?.hideHeadline !== undefined ? 'complete' : 'incomplete';
      case 'leaderboard':
        return options?.leaderboard !== undefined ? 'complete' : 'incomplete';
      case 'leadCapture':
        return options?.ctaEnabled !== undefined ? 'complete' : 'incomplete';
      case 'sharing':
        return 'complete'; // Sharing is always available
      default:
        return 'incomplete';
    }
  };

  // Form sections definition with categories and priority
  const formSections = React.useMemo(() => [
    {
      category: 'Game',
      sections: [
        {
          id: 'appearance',
          title: 'Appearance',
          icon: <SwatchIcon className="w-5 h-5" />,
          component: AppearanceSection,
          props: { options, onOptionsUpdate, game },
          priority: 'medium',
          status: getSectionStatus('appearance')
        },
        {
          id: 'content',
          title: 'Start Screen',
          icon: <PaintBrushIcon className="w-5 h-5" />,
          component: StartScreenSection,
          props: { options, onOptionsUpdate, params, campaign: game },
          priority: 'high',
          status: getSectionStatus('content'),
          required: true
        },
        {
          id: 'gameSetup',
          title: 'Game Assets',
          icon: <CogIcon className="w-5 h-5" />,
          component: GameSetupComponent,
          props: { options, onOptionsUpdate },
          priority: 'high',
          status: getSectionStatus('gameSetup')
        }
      ]
    },
    {
      category: 'Engagement',
      sections: [
        {
          id: 'leadCapture',
          title: 'Action Button',
          icon: <MegaphoneIcon className="w-5 h-5" />,
          component: CTASection,
          props: { options, onOptionsUpdate },
          badge: options?.ctaEnabled ? 'enabled' : 'disabled',
          priority: 'medium',
          status: getSectionStatus('leadCapture')
        },
        {
          id: 'leaderboard',
          title: 'Leaderboard',
          icon: <TableCellsIcon className="w-5 h-5" />,
          component: LeaderboardSection,
          props: { options, onOptionsUpdate },
          badge: options?.leaderboard?.enabled ? 'enabled' : 'disabled',
          priority: 'medium',
          status: getSectionStatus('leaderboard')
        },
        {
          id: 'sharing',
          title: 'Sharing',
          icon: <ShareIcon className="w-5 h-5" />,
          component: ShareSection,
          props: { options, onOptionsUpdate, params },
          priority: 'low',
          status: getSectionStatus('sharing')
        }
      ]
    },
  ], [options, onOptionsUpdate, params, GameSetupComponent, game]);

  // Switching game type is a destructive re-base, not a normal edit.
  // It lives pinned at the bottom of the sidebar as a footer utility,
  // styled less prominently than the main sections — but still uses
  // the same activeSection routing so renderSectionContent shows the
  // type-picker UI when clicked.
  const gameTypeSection = React.useMemo(() => ({
    id: 'gameType',
    title: 'Switch game type',
    icon: <PuzzlePieceIcon className="w-4 h-4" />,
    component: null,
    props: {},
    priority: 'low',
  }), []);


  // Render the active section's content
  const renderSectionContent = (section) => {
    if (section.id === 'gameType') {
      return (
        <div>
          <p className="text-xs gch-text-secondary mb-4">Switch game type. Your brand, theme, and share settings carry over. Game-specific text and assets reset to the new type's defaults.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {allGameTypes.map((type) => {
              const isActive = type.id === game?.gameType;
              const colors = getGameTypeColors(type.id);
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => {
                    if (!isActive && onSwitchGameType) {
                      onSwitchGameType(type);
                      setActiveSection('appearance');
                    }
                  }}
                  className={`w-full text-left rounded-lg overflow-hidden flex items-center gap-3 p-2.5 border transition-all ${
                    isActive
                      ? 'border-[var(--admin-accent)]/60 bg-[var(--admin-accent)]/10'
                      : 'border-white/10 hover:border-white/20 hover:bg-white/5 cursor-pointer'
                  }`}
                >
                  <div
                    className="w-10 h-14 rounded flex-shrink-0 overflow-hidden flex items-center justify-center"
                    style={{ background: colors?.bg || 'var(--admin-bg-surface)' }}
                  >
                    {type.imageUrl ? (
                      <img src={type.imageUrl} alt="" className="w-full h-full object-cover" />
                    ) : type.icon ? (
                      <type.icon className="w-5 h-5" style={{ color: colors?.icon }} />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${isActive ? 'text-[var(--admin-accent)]' : 'gch-text-primary'}`}>
                      {type.name}
                    </div>
                    <div className="text-xs gch-text-secondary truncate mt-0.5 line-clamp-2">{type.description}</div>
                  </div>
                  {isActive && (
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--admin-accent)] flex items-center justify-center">
                      <span className="text-[10px] font-bold" style={{ color: 'var(--admin-bg-surface)' }}>✓</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Game Assets: loading state, retry on chunk load error (common on slow/mobile networks), or no gameType
    if (section.id === 'gameSetup') {
      if (game?.gameType && !section.component && !gameSetupLoadError) {
        return (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-8 h-8 border-3 border-white/10 border-t-[var(--admin-accent)] rounded-full animate-spin" />
              <p className="text-sm font-semibold gch-text-primary">Loading Game Assets...</p>
            </div>
          </div>
        );
      }
      if (game?.gameType && gameSetupLoadError && gameSetupAutoRetrying) {
        return (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-8 h-8 border-3 border-white/10 border-t-[var(--admin-accent)] rounded-full animate-spin" />
              <p className="text-sm font-semibold gch-text-primary">Retrying Game Assets...</p>
            </div>
          </div>
        );
      }
      if (game?.gameType && gameSetupLoadError) {
        return (
          <div className="flex items-center justify-center h-64 gch-text-secondary">
            <div className="text-center max-w-sm px-4">
              <CogIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm font-medium gch-text-primary mb-1">Couldn't load game assets</p>
              <p className="text-xs gch-text-muted mb-4">This can happen on slow or unstable connections. Tap Retry to try again.</p>
              <button
                type="button"
                onClick={retryGameSetupLoad}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--admin-accent)] text-white hover:opacity-90 transition-opacity"
              >
                Retry
              </button>
            </div>
          </div>
        );
      }
      if (!section.component) {
        // No game type: show loading when game data not yet loaded (empty object), to avoid "not available" flash on mobile
        const gameNotYetLoaded = game && typeof game === 'object' && Object.keys(game).length === 0;
        return (
          <div className="flex items-center justify-center h-64 gch-text-secondary">
            <div className="text-center">
              {gameNotYetLoaded ? (
                <>
                  <div className="w-8 h-8 border-3 border-white/10 border-t-[var(--admin-accent)] rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-sm font-medium gch-text-primary">Loading game...</p>
                </>
              ) : (
                <>
                  <CogIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm font-medium">This section is not available for this game type.</p>
                </>
              )}
            </div>
          </div>
        );
      }
    } else if (!section.component) {
      return (
        <div className="flex items-center justify-center h-64 gch-text-secondary">
          <div className="text-center">
            <CogIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm font-medium">This section is not available for this game type.</p>
          </div>
        </div>
      );
    }

    const Component = section.component;
    return (
      <Suspense fallback={
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-8 h-8 border-3 border-white/10 border-t-[var(--admin-accent)] rounded-full animate-spin" />
            <p className="text-sm font-semibold gch-text-primary">
              Loading...
            </p>
          </div>
        </div>
      }>
        <Component {...section.props} />
      </Suspense>
    );
  };

  // Flatten sections for easier access
  const allSections = [
    ...formSections.flatMap(category => category.sections),
    gameTypeSection,
  ];
  const activeTab = allSections.find(s => s.id === activeSection);

  return (
    <div className="h-full flex flex-col md:flex-row">
      {/* Desktop-only first-run coach-mark. Self-checks viewport + dismissed
          state, so safe to mount unconditionally. */}
      <EditorTour campaign={game} activeSection={activeSection} setActiveSection={setActiveSection} />
      {/* Mobile Navigation - Compact horizontal tabs with edge-fade scroll affordance */}
      <div className="md:hidden flex-shrink-0 border-b border-[var(--admin-border)] gch-bg-card">
        <ScrollableTabBar fadeFrom="var(--admin-card-bg)" mobileOnly={false}>
          <div ref={mobileNavRef} className="flex gap-1.5 px-3 py-2.5 min-w-max">
            {allSections.map((section) => (
              <button
                key={section.id}
                type="button"
                data-active-tab={activeSection === section.id ? 'true' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveSection(section.id);
                }}
                className={`flex-shrink-0 rounded-lg transition-colors duration-200 min-w-0 px-3.5 py-2.5 min-h-[40px] focus:outline-none focus:ring-0 ${
                  section.accent
                    ? 'text-white font-semibold'
                    : (activeSection === section.id
                        ? 'bg-[var(--admin-accent-light)] text-[var(--admin-accent)] border border-[var(--admin-accent)]/40'
                        : 'border border-transparent gch-text-secondary hover:bg-white/10 hover:gch-text-primary')
                }`}
                style={section.accent ? {
                  background: 'var(--admin-cta-gradient)',
                  border: 'var(--admin-cta-border-width) solid var(--admin-cta-border)',
                  boxShadow: 'var(--admin-cta-shadow)',
                } : undefined}
              >
                <div className="flex items-center gap-1.5">
                  <div className="flex-shrink-0">
                    {React.cloneElement(section.icon, { className: 'w-4 h-4' })}
                  </div>
                  <span className="font-semibold text-[13px] whitespace-nowrap">
                    {section.title}
                  </span>
                  {section.badge && (
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      section.badge === 'enabled' ? 'bg-green-400' : 'bg-white/30'
                    }`} title={section.badge === 'enabled' ? 'Enabled' : 'Disabled'} />
                  )}
                </div>
              </button>
            ))}
          </div>
        </ScrollableTabBar>
      </div>

      {/* Desktop Navigation - Compact sidebar, icon + title only */}
      <div className={`hidden md:flex flex-shrink-0 flex-col border-r border-[var(--admin-border)] transition-[width] duration-300 overflow-hidden ${
        navCollapsed ? 'w-16' : 'w-48'
      }`}>
        <nav className="flex-1 p-2 overflow-y-auto min-w-0">
          <div className={navCollapsed ? 'space-y-1' : 'space-y-3'}>
            {formSections.map((category) => (
              <div key={category.category} className={navCollapsed ? 'space-y-0.5' : 'space-y-0.5'}>
                {!navCollapsed && category.sections.length > 1 && (
                  <div className="px-2 py-1">
                    <span className="text-[10px] font-bold gch-text-muted uppercase tracking-wider">
                      {category.category}
                    </span>
                  </div>
                )}
                <div className="space-y-0.5">
                  {category.sections.map((section) => {
                    return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSection(section.id);
                      }}
                      title={navCollapsed ? section.title : undefined}
                      style={activeSection === section.id ? { borderLeft: '2px solid var(--admin-accent)' } : { borderLeft: '2px solid transparent' }}
                      className={`w-full group relative rounded-l-none rounded-r-md transition-colors duration-150 focus:outline-none focus:ring-0 ${
                        navCollapsed ? 'flex justify-center p-2' : ''
                      } ${
                        activeSection === section.id
                          ? 'bg-[var(--admin-accent)]/10 text-[var(--admin-accent)]'
                          : 'gch-text-secondary hover:bg-white/5 hover:gch-text-primary'
                      }`}
                    >
                      <div className={`flex items-center ${navCollapsed ? 'justify-center' : 'gap-2.5 px-2 py-1.5'}`}>
                        <div className="flex-shrink-0">
                          {React.cloneElement(section.icon, {
                            className: `${navCollapsed ? 'w-5 h-5' : 'w-4 h-4'} ${activeSection === section.id ? 'text-[var(--admin-accent)]' : 'text-current'}`
                          })}
                        </div>
                        {!navCollapsed && (
                          <>
                            <span className={`font-medium text-[13px] flex-1 text-left truncate ${
                              activeSection === section.id ? 'text-[var(--admin-accent)]' : 'gch-text-primary'
                            }`}>
                              {section.title}
                            </span>
                            {section.badge ? (
                              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                section.badge === 'enabled' ? 'bg-green-400' : 'bg-white/20'
                              }`} title={section.badge === 'enabled' ? 'Enabled' : 'Disabled'} />
                            ) : section.status ? (
                              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                section.status === 'complete' ? 'bg-green-400' : 'bg-white/20'
                              }`} title={section.status === 'complete' ? 'Complete' : 'Needs attention'} />
                            ) : null}
                          </>
                        )}
                      </div>
                    </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Collapse toggle */}
        {onNavCollapsedChange && (
          <div className={`flex-shrink-0 border-t border-[var(--admin-border)] p-1.5 ${navCollapsed ? 'flex justify-center' : ''}`}>
            <button
              type="button"
              onClick={onNavCollapsedChange}
              className="p-1.5 rounded-md transition-all duration-200 gch-text-muted hover:gch-text-primary hover:bg-white/10 w-full flex items-center justify-center"
              title={navCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {navCollapsed ? (
                <ChevronDoubleRightIcon className="w-3.5 h-3.5" />
              ) : (
                <ChevronDoubleLeftIcon className="w-3.5 h-3.5 flex-shrink-0" />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Content Area - No card wrapper, controls sit directly on surface */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 studio-scrollbar overscroll-contain scroll-content-safe-bottom">
        {activeTab && (
          <div className="p-5 md:p-5 min-w-0 w-full max-w-none">
            {/* Section Content - form for validation, no card wrapper.
                Wrapped in AnimatePresence so swapping sections cross-fades
                instead of hard-cutting. Keyed by activeSection so each
                panel transitions independently. */}
            <form ref={formRef}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSection}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  className="space-y-5"
                >
                  {renderSectionContent(activeTab)}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        )}
      </div>

    </div>
  );
};

export default Form;
